import { useEffect, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { NotificationListResponse } from '../../lib/types/topbar.js';
import { usePopoverState } from './usePopoverState.js';
import { PopoverAnchor } from './PopoverAnchor.js';
import { SearchPopover } from './SearchPopover.js';
import { QuickAddPopover } from './QuickAddPopover.js';
import { MyTasksPopover } from './MyTasksPopover.js';
import { QuickCalendarPopover } from './QuickCalendarPopover.js';
import { NotificationsPopover } from './NotificationsPopover.js';
import { UserMenuPopover } from './UserMenuPopover.js';
import { UserAvatar, type UserAvatarSource } from '../../lib/avatars.js';

type PopoverKey = 'search' | 'add' | 'work' | 'cal' | 'bell' | 'user';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html — the six topbar
 * global-utility popovers assembled with real single-open/outside-click/Esc
 * behavior (usePopoverState) and RTL-anchored panels (PopoverAnchor). Each
 * popover's own file documents its real-vs-not-built boundary; see them
 * for the per-feature detail.
 *
 * Rendered inside V2Topbar's `actions` slot in AuthenticatedShell.tsx —
 * this replaces that shell's previous static, non-interactive bell/user
 * block.
 */
export function TopbarPopovers({
  slug,
  entitledModuleSlugs,
  userName,
  userRole,
  avatar,
}: {
  slug: string;
  entitledModuleSlugs: readonly string[];
  userName: string;
  userRole: string;
  avatar: UserAvatarSource | null;
}) {
  const { open, toggle, openOnly, close, containerRef } = usePopoverState<PopoverKey>();
  const [q, setQ] = useState('');
  const base = tenantBasePath(slug);

  // Cheap, real unread signal for the bell's dot — refetched whenever the
  // notifications popover closes (covers "mark (all) read" while open).
  const [unreadRefresh, setUnreadRefresh] = useState(0);
  const unread = useApiGet<NotificationListResponse>(
    `${base}/api/v1/notifications?unreadOnly=true&limit=1`,
    [unreadRefresh],
  );
  useEffect(() => {
    if (open !== 'bell') setUnreadRefresh((k) => k + 1);
  }, [open]);
  const hasUnread = unread.status === 'ready' && unread.data.notifications.length > 0;

  return (
    <div ref={containerRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <style>
        {
          '@keyframes sk-pop-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}'
        }
      </style>

      <PopoverAnchor
        align="start"
        width={560}
        open={open === 'search'}
        onClose={close}
        trigger={
          <div
            className="sk-input-group sk-topbar__search"
            // v2 board: clamp(170px, 24vw, 280px). The <900px mobile rule
            // (components.css) still pins this to 132px with !important.
            style={{ width: 'clamp(170px, 24vw, 280px)' }}
            onClick={openOnly('search')}
          >
            <Icon name="search" size="sm" />
            <input
              placeholder="جست‌وجو..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={openOnly('search')}
              // min-width: 0 lets the input shrink with its group on a phone
              // (the group narrows below its 220px); without it the input's
              // intrinsic width overflowed the topbar and the shell scrolled
              // sideways.
              style={{ fontSize: 13, width: '100%', minWidth: 0 }}
            />
          </div>
        }
      >
        <SearchPopover slug={slug} q={q} onQ={setQ} />
      </PopoverAnchor>

      {/* Each of these has its own mobile-shell equivalent (MOBILE_NAV's
          bottom-nav route) — the sk-topbar__actions class hides the group
          under the mobile breakpoint (see components.css) so this bar
          doesn't overflow there; search above has no other home, so it
          stays outside this wrapper. */}
      <div className="sk-topbar__actions">
        <PopoverAnchor
          align="start"
          width={360}
          open={open === 'add'}
          onClose={close}
          trigger={
            <button className="sk-btn sk-btn--primary sk-btn--sm" onClick={toggle('add')}>
              <Icon name="plus" size="sm" />
              افزودن سریع
            </button>
          }
        >
          <QuickAddPopover slug={slug} entitledModuleSlugs={entitledModuleSlugs} onClose={close} />
        </PopoverAnchor>

        <PopoverAnchor
          align="end"
          width={380}
          open={open === 'work'}
          onClose={close}
          trigger={
            <button
              className="sk-btn sk-btn--ghost sk-btn--icon"
              onClick={toggle('work')}
              aria-label="کارهای من"
            >
              <Icon name="check" size="md" />
            </button>
          }
        >
          <MyTasksPopover slug={slug} open={open === 'work'} onNavigate={close} />
        </PopoverAnchor>

        <PopoverAnchor
          align="end"
          width={380}
          open={open === 'cal'}
          onClose={close}
          trigger={
            <button
              className="sk-btn sk-btn--ghost sk-btn--icon"
              onClick={toggle('cal')}
              aria-label="تقویم سریع"
            >
              <Icon name="calendar" size="md" />
            </button>
          }
        >
          <QuickCalendarPopover slug={slug} open={open === 'cal'} />
        </PopoverAnchor>

        <PopoverAnchor
          align="end"
          width={380}
          open={open === 'bell'}
          onClose={close}
          trigger={
            <button
              className="sk-btn sk-btn--ghost sk-btn--icon"
              onClick={toggle('bell')}
              aria-label="اعلان‌ها"
              style={{ position: 'relative' }}
            >
              <Icon name="bell" size="md" />
              {hasUnread ? (
                <span
                  style={{
                    position: 'absolute',
                    top: 7,
                    insetInlineEnd: 7,
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--sk-danger)',
                    border: '1.5px solid var(--sk-bg-elev)',
                  }}
                />
              ) : null}
            </button>
          }
        >
          <NotificationsPopover slug={slug} open={open === 'bell'} />
        </PopoverAnchor>

        <PopoverAnchor
          align="end"
          width={300}
          open={open === 'user'}
          onClose={close}
          trigger={
            <div className="sk-topbar__user" onClick={toggle('user')} style={{ cursor: 'pointer' }}>
              <UserAvatar name={userName} source={avatar} size={30} />
              <div
                className="sk-topbar__user-text"
                style={{ display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <span style={{ fontSize: 13, fontWeight: 500 }}>{userName}</span>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>{userRole}</span>
              </div>
              <span style={{ color: 'var(--sk-fg-subtle)', display: 'flex' }}>
                <Icon name="chevronDown" size="sm" />
              </span>
            </div>
          }
        >
          <UserMenuPopover slug={slug} userName={userName} userRole={userRole} avatar={avatar} />
        </PopoverAnchor>
      </div>
    </div>
  );
}
